import { DEFAULT_EVENT_CONFIG } from "./defaultConfig";
import { extractListItems } from "./rewardsMappers";

const REFERRAL_LEVELS = ["L1", "L2", "L3", "L4", "L5"];

const REFERENCE_TYPES = {
  SIGNUP_WITH_REFERRAL: "SIGNUP_WITH_REFERRAL",
  SIGNUP_WITHOUT_REFERRAL: "SIGNUP_WITHOUT_REFERRAL",
  REFERRAL_SIGNUP: "REFERRAL_SIGNUP",
};

const REFERRAL_REFERENCE_TYPES = new Set(
  Object.entries(DEFAULT_EVENT_CONFIG)
    .filter(([, config]) => config.cat === "Referral rewards")
    .map(([key]) => key)
);
REFERRAL_REFERENCE_TYPES.add(REFERENCE_TYPES.REFERRAL_SIGNUP);

const EMPTY_METRICS = {
  totalCoinsEarned: 0,
  signUpsWithReferral: 0,
  signUpsWithoutReferral: 0,
  firstApprovedShifts: 0,
  firstApprovedShiftCoins: 0,
  usersEligibleToRedeem: 0,
  totalAvailable: 0,
  totalRedeemed: 0,
  totalExpired: 0,
  blockedByLock: 0,
  totalReversed: 0,
  referralIssued: 0,
  referralExpired: 0,
  inviteSharedViaApp: 0,
  rewardsCoinsIssued: 0,
  rewardsCoinsExpired: 0,
};

export const EMPTY_ANALYTICS_DASHBOARD = {
  metrics: { ...EMPTY_METRICS },
  eventRows: [],
  referralFunnel: [],
  levelRows: [],
  users: [],
  rewardEvents: [],
  cities: [],
  professions: [],
  redemptionData: {
    totalUser: 0,
    eligibleUserCount: 0,
    notEligibleUserCount: 0,
    eligibleUsers: [],
    notEligibleUsers: [],
  },
};

function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function pickValue(source, keys) {
  if (!source || typeof source !== "object") return undefined;
  for (const key of keys) {
    if (source[key] != null && source[key] !== "") return source[key];
  }
  return undefined;
}

function pickNumber(source, keys) {
  const value = pickValue(source, keys);
  return value == null ? 0 : toNumber(value);
}

function pickNonEmptyString(...values) {
  for (const value of values) {
    const text = String(value ?? "").trim();
    if (text) return text;
  }
  return "";
}

function resolveUserProfile(raw) {
  const profile = raw?.user ?? raw?.userDetails ?? null;
  return profile && typeof profile === "object" ? profile : null;
}

function resolveUserObject(raw) {
  return resolveUserProfile(raw);
}

function resolveUserId(raw) {
  if (raw?.userId != null && String(raw.userId).trim()) {
    return String(raw.userId).trim();
  }

  const profile = resolveUserProfile(raw);
  if (profile?._id != null && String(profile._id).trim()) return String(profile._id).trim();
  if (profile?.id != null && String(profile.id).trim()) return String(profile.id).trim();

  if (typeof raw?.user === "string" || typeof raw?.user === "number") {
    return String(raw.user);
  }

  return String(raw?.userRef ?? "unknown");
}

function formatEventLabel(key) {
  const normalized = String(key ?? "").trim();
  if (!normalized) return "";

  const config = DEFAULT_EVENT_CONFIG[normalized];
  if (config?.name) return config.name;

  return normalized
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatPersonName(name) {
  if (!name) return "";
  if (typeof name === "string") return name.trim();
  if (typeof name !== "object") return "";

  const salutation = name.salutation ? `${String(name.salutation).replace(/\.$/, "")}.` : "";
  const first = name.first ? String(name.first).trim() : "";
  const last = name.last ? String(name.last).trim() : "";
  const composed = [salutation, first, last].filter(Boolean).join(" ").trim();
  if (composed) return composed;
  return String(name.full || "").trim();
}

function formatDateValue(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toISOString().slice(0, 10);
}

function normalizeTransactionType(rawType, amount) {
  const type = String(rawType || "").toLowerCase();
  if (type.includes("credit") || type === "credit") return "Credit";
  if (type.includes("debit") || type === "debit" || amount < 0) return "Debit";
  return amount >= 0 ? "Credit" : "Debit";
}

function normalizeStatus(rawStatus, type, raw) {
  if (raw?.isExpired) return "Expired";
  const source = String(raw?.source ?? "").toUpperCase();
  if (source === "EXPIRY") return "Expired";

  const status = String(rawStatus || "").trim();
  if (status) return status;
  return "Completed";
}

function resolveTransactionEvent(raw) {
  const description = pickNonEmptyString(raw?.description, raw?.title, raw?.event);
  if (description) return description;

  const referenceType = pickNonEmptyString(raw?.referenceType);
  if (referenceType) return formatEventLabel(referenceType);

  const source = pickNonEmptyString(raw?.source);
  if (source && source.toUpperCase() !== "REWARD") return formatEventLabel(source);

  return "Wallet transaction";
}

function mapTransactionRow(raw) {
  const amount = toNumber(raw?.amount ?? raw?.coins);
  const type = normalizeTransactionType(raw?.transactionType ?? raw?.type, amount);
  return {
    date: formatDateValue(raw?.date ?? raw?.createdAt ?? raw?.occurredAt),
    type,
    event: resolveTransactionEvent(raw),
    coins: Math.abs(amount),
    status: normalizeStatus(raw?.status, type, raw),
  };
}

function mapReferralLevels(raw) {
  const levels = { L1: 0, L2: 0, L3: 0, L4: 0, L5: 0 };
  if (!raw || typeof raw !== "object") return levels;

  REFERRAL_LEVELS.forEach((level) => {
    const key = level.toLowerCase();
    levels[level] = toNumber(raw[level] ?? raw[key] ?? raw[`${key}Coins`] ?? raw[`level${level.slice(1)}`]);
  });

  return levels;
}

function mapRewardEvents(raw) {
  if (!raw || typeof raw !== "object") return {};
  return Object.entries(raw).reduce((acc, [key, value]) => {
    acc[key] = toNumber(value);
    return acc;
  }, {});
}

function mapAnalyticsUser(raw) {
  if (!raw) return null;

  const userObj = resolveUserObject(raw) ?? {};
  const userId = resolveUserId(raw);
  const name =
    pickValue(raw, ["name", "fullName"]) ??
    pickValue(userObj, ["fullName", "name"]) ??
    formatPersonName(userObj?.name) ??
    (userId !== "unknown" ? `User ${userId.slice(-6)}` : "");
  const referralCode =
    pickValue(raw, ["referralCode", "referCode"]) ??
    pickValue(userObj, ["referralCode", "referCode"]) ??
    "";
  const rewardsCoinsEarned = pickNumber(raw, [
    "rewardsCoinsEarned",
    "rewardCoinsEarned",
    "rewardsEarned",
  ]);
  const referralCoinsEarned = pickNumber(raw, [
    "referralCoinsEarned",
    "referralEarned",
    "referralCoins",
  ]);
  const coinsRedeemed = pickNumber(raw, ["coinsRedeemed", "redeemed", "redeemedCoins"]);
  const coinsExpired = pickNumber(raw, ["coinsExpired", "expired", "expiredCoins"]);
  const coinsReversed = pickNumber(raw, ["coinsReversed", "reversed", "reversedCoins"]);
  const blockedByLockCoins = pickNumber(raw, ["blockedByLockCoins", "blockedByLock", "lockedCoins"]);
  const transactions = (raw.transactions ?? raw.walletTransactions ?? raw.history ?? [])
    .map(mapTransactionRow)
    .filter(Boolean);

  return {
    id: userId,
    name: String(name || "Unknown user"),
    referralCode: String(referralCode || "—"),
    phone:
      pickValue(raw, ["phone", "mobileNumber"]) ??
      pickValue(userObj, ["mobileNumber", "phone"]) ??
      userObj?.phoneNumbers?.[0]?.number ??
      "",
    email:
      pickValue(raw, ["email"]) ??
      userObj?.emails?.[0]?.address ??
      userObj?.email ??
      "",
    city:
      pickValue(raw, ["city", "workCity"]) ??
      pickValue(userObj, ["workCity", "city"]) ??
      userObj?.city?.name ??
      userObj?.address?.city ??
      userObj?.correspondenceAddress?.city ??
      "",
    profession:
      pickValue(raw, ["profession"]) ??
      pickValue(userObj, ["profession"]) ??
      userObj?.professionalType?.name ??
      "",
    qualification:
      pickValue(raw, ["qualification"]) ??
      pickValue(userObj, ["qualification"]) ??
      userObj?.qualification?.name ??
      "",
    specialisation:
      pickValue(raw, ["specialisation", "specialization"]) ??
      pickValue(userObj, ["specialisation", "specialization"]) ??
      userObj?.specialisation?.name ??
      userObj?.specialization?.name ??
      "",
    status: pickValue(raw, ["status"]) ?? (transactions.length ? "Active" : "Inactive") ?? "Inactive",
    signupDate: formatDateValue(raw.signupDate ?? userObj?.createdAt),
    usedReferralCode: Boolean(
      pickValue(raw, ["usedReferralCode", "hasReferralCode"]) ?? raw.usedReferral ?? false
    ),
    signedUpWithoutReferral: Boolean(
      pickValue(raw, ["signedUpWithoutReferral", "signupWithoutReferral"]) ?? false
    ),
    firstApprovedShiftDate: formatDateValue(raw.firstApprovedShiftDate ?? raw.firstShiftDate),
    rewardsCoinsEarned,
    referralCoinsEarned,
    coinsRedeemed,
    coinsExpired,
    coinsReversed,
    blockedByLockCoins,
    referralLevels: mapReferralLevels(raw.referralLevels ?? raw.levelCoins ?? raw),
    rewardEvents: mapRewardEvents(raw.rewardEvents ?? raw.eventRewards),
    transactions,
  };
}

function mapEventRow(raw) {
  if (Array.isArray(raw)) {
    const [eventName, coinsIssued, usersRewarded, users] = raw;
    const rewarded = toNumber(usersRewarded);
    const coins = toNumber(coinsIssued);
    return {
      eventName: String(eventName || ""),
      usersRewarded: rewarded,
      coinsIssued: coins,
      avg: rewarded > 0 ? (coins / rewarded).toFixed(2) : "0.00",
      users: Array.isArray(users) ? users : [],
    };
  }

  const usersRewarded = pickNumber(raw, ["usersRewarded", "users", "userCount"]);
  const coinsIssued = pickNumber(raw, ["coinsIssued", "coins", "totalCoins"]);
  return {
    eventName: String(pickValue(raw, ["eventName", "name", "event", "rewardEvent"]) || ""),
    usersRewarded,
    coinsIssued,
    avg:
      raw?.avg ??
      raw?.avgCoinsPerUser ??
      (usersRewarded > 0 ? (coinsIssued / usersRewarded).toFixed(2) : "0.00"),
    users: Array.isArray(raw?.userDetails)
      ? raw.userDetails
      : Array.isArray(raw?.users) && raw.users.every((u) => u && typeof u === "object")
        ? raw.users
        : [],
  };
}

function toUserDetail(raw) {
  if (!raw) return null;
  if (typeof raw === "string" || typeof raw === "number") {
    return {
      id: String(raw),
      name: `User ${String(raw).slice(-6)}`,
      phone: "",
      profession: "",
      qualification: "",
      specialisation: "",
      city: "",
    };
  }

  const id = String(raw.userId ?? raw.id ?? raw._id ?? "unknown");
  return {
    id,
    name: String(
      pickValue(raw, ["name", "fullName"]) ||
        (id !== "unknown" ? `User ${id.slice(-6)}` : "Unknown user")
    ),
    phone: String(pickValue(raw, ["phone", "mobileNumber"]) || ""),
    profession: String(pickValue(raw, ["profession"]) || ""),
    qualification: String(pickValue(raw, ["qualification"]) || ""),
    specialisation: String(
      pickValue(raw, ["specialisation", "specialization"]) || ""
    ),
    city: String(pickValue(raw, ["city", "workCity"]) || ""),
    coinsEarned: toNumber(
      raw.coinsEarned ??
        raw.coins ??
        (toNumber(raw.rewardsCoinsEarned) + toNumber(raw.referralCoinsEarned))
    ),
  };
}

function mapLevelRow(raw) {
  if (Array.isArray(raw)) {
    const [level, coinsIssued, peopleJoined] = raw;
    const people = toNumber(peopleJoined);
    const coins = toNumber(coinsIssued);
    return {
      level: String(level || ""),
      peopleJoined: people,
      coinsIssued: coins,
      avg: people > 0 ? (coins / people).toFixed(2) : "0.00",
    };
  }

  const peopleJoined = pickNumber(raw, ["peopleJoined", "usersJoined", "joinCount", "users"]);
  const coinsIssued = pickNumber(raw, ["coinsIssued", "coins", "totalCoins"]);
  return {
    level: String(pickValue(raw, ["level", "referralLevel"]) || ""),
    peopleJoined,
    coinsIssued,
    avg:
      raw?.avg ??
      raw?.avgCoinsPerJoin ??
      (peopleJoined > 0 ? (coinsIssued / peopleJoined).toFixed(2) : "0.00"),
  };
}

function mapFunnelSteps(rawSteps) {
  const steps = (Array.isArray(rawSteps) ? rawSteps : [])
    .map((row) => {
      if (Array.isArray(row)) {
        return {
          step: String(row[0] || ""),
          count: toNumber(row[1]),
          coins: row[2] != null ? toNumber(row[2]) : null,
        };
      }
      return {
        step: String(pickValue(row, ["step", "name", "label"]) || ""),
        count: pickNumber(row, ["count", "value", "total"]),
        coins:
          row?.coins != null || row?.coinsIssued != null
            ? toNumber(row.coins ?? row.coinsIssued)
            : null,
      };
    })
    .filter((row) => row.step);

  const base = steps[0]?.count || 0;
  return steps.map((row, idx) => {
    const previous = idx === 0 ? row.count : steps[idx - 1]?.count || 0;
    const conversion =
      idx === 0
        ? "100%"
        : previous > 0
          ? `${((row.count / previous) * 100).toFixed(1)}%`
          : "0.0%";
    const share = base > 0 ? `${((row.count / base) * 100).toFixed(1)}%` : "0.0%";
    return { ...row, conversion, share };
  });
}

function mapMetrics(raw = {}) {
  return {
    totalCoinsEarned: pickNumber(raw, ["totalCoinsEarned", "coinsEarned"]),
    signUpsWithReferral: pickNumber(raw, ["signUpsWithReferral", "signUpsWithReferralCode"]),
    signUpsWithoutReferral: pickNumber(raw, ["signUpsWithoutReferral", "signUpsWithoutReferralCode"]),
    firstApprovedShifts: pickNumber(raw, ["firstApprovedShifts", "firstApprovedShift"]),
    firstApprovedShiftCoins: pickNumber(raw, ["firstApprovedShiftCoins", "firstShiftCoins"]),
    usersEligibleToRedeem: pickNumber(raw, ["usersEligibleToRedeem", "eligibleUsers"]),
    totalAvailable: pickNumber(raw, ["totalAvailable", "redeemable", "availableCoins"]),
    totalRedeemed: pickNumber(raw, ["totalRedeemed", "alreadyRedeemedCoins", "redeemedCoins"]),
    totalExpired: pickNumber(raw, ["totalExpired", "expired", "expiredCoins"]),
    blockedByLock: pickNumber(raw, ["blockedByLock", "blockedCoins", "lockedCoins"]),
    totalReversed: pickNumber(raw, ["totalReversed", "reversed", "reversedCoins"]),
    referralIssued: pickNumber(raw, ["referralIssued", "referralCoinsIssued"]),
    referralExpired: pickNumber(raw, ["referralExpired", "referralCoinsExpired"]),
    inviteSharedViaApp: pickNumber(raw, ["inviteSharedViaApp", "inviteShared"]),
    rewardsCoinsIssued: pickNumber(raw, ["rewardsCoinsIssued", "rewardCoinsIssued"]),
    rewardsCoinsExpired: pickNumber(raw, ["rewardsCoinsExpired", "rewardCoinsExpired"]),
  };
}

function uniqueStrings(values) {
  return [...new Set(values.filter(Boolean))].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base" })
  );
}

function deriveFilterOptions(users) {
  return {
    cities: uniqueStrings(users.map((user) => user.city)),
    professions: uniqueStrings(users.map((user) => user.profession)),
  };
}

function deriveRewardEvents(users, eventRows) {
  const fromRows = eventRows
    .map((row) => (typeof row === "string" ? row : row.eventName))
    .filter(Boolean);
  const fromUsers = users.flatMap((user) => Object.keys(user.rewardEvents || {}));
  return uniqueStrings([...fromRows, ...fromUsers]);
}

function getTransactionSource(tx) {
  return String(tx?.source ?? "")
    .trim()
    .toUpperCase();
}

function getReferenceType(tx) {
  return String(tx?.referenceType ?? "").trim();
}

function isReferralCreditTransaction(tx) {
  const source = getTransactionSource(tx);
  const referenceType = getReferenceType(tx);

  if (source === "REFERRAL") return true;
  if (referenceType === REFERENCE_TYPES.REFERRAL_SIGNUP) return true;
  if (referenceType && REFERRAL_REFERENCE_TYPES.has(referenceType)) return true;

  return false;
}

function isRewardCreditTransaction(tx) {
  return getTransactionSource(tx) === "REWARD";
}

function isSignupWithReferralTransaction(tx) {
  return getReferenceType(tx) === REFERENCE_TYPES.SIGNUP_WITH_REFERRAL;
}

function isSignupWithoutReferralTransaction(tx) {
  return getReferenceType(tx) === REFERENCE_TYPES.SIGNUP_WITHOUT_REFERRAL;
}

function isFirstApprovedShiftTransaction(tx) {
  const referenceType = getReferenceType(tx).toUpperCase();
  if (referenceType) {
    if (
      referenceType === "FIRST_SHIFT" ||
      referenceType === "FIRST_APPROVED_SHIFT" ||
      referenceType === "FIRST_LOCUM_BGV_APPROVED" ||
      referenceType.includes("FIRST_SHIFT") ||
      referenceType.includes("FIRST_APPROVED_SHIFT")
    ) {
      return true;
    }
  }

  const idempotencyKey = String(tx?.idempotencyKey ?? "");
  return /^FIRST_SHIFT_/i.test(idempotencyKey);
}

function getFirstApprovedShiftUserId(tx) {
  if (!isFirstApprovedShiftTransaction(tx)) return null;
  return getReferralJoinedUserId(tx) || resolveUserId(tx);
}

function buildReferralFunnel({
  codeCaptured,
  successfulSignUp,
  firstApprovedShift,
  firstApprovedShiftCoins = 0,
}) {
  return mapFunnelSteps([
    ["Code Captured", codeCaptured],
    ["Successful Sign-Up", successfulSignUp],
    ["First Approved Shift", firstApprovedShift, firstApprovedShiftCoins],
  ]);
}

function isExpiryTransaction(tx) {
  if (tx?.isExpired) return true;
  const source = String(tx.source ?? "").toUpperCase();
  if (source === "EXPIRY") return true;

  const haystack = `${tx.source || ""} ${tx.transactionType || ""} ${tx.referenceType || ""} ${
    tx.description || ""
  }`.toLowerCase();
  return haystack.includes("expir");
}

function isReversalTransaction(tx) {
  const source = getTransactionSource(tx);
  const type = String(tx?.transactionType || tx?.type || "")
    .trim()
    .toUpperCase();
  if (source === "REVERSAL" || type === "REVERSAL") return true;

  const haystack = `${tx?.source || ""} ${tx?.transactionType || ""} ${tx?.referenceType || ""} ${
    tx?.description || ""
  }`.toLowerCase();
  return haystack.includes("revers");
}

function isRedemptionTransaction(tx, amount) {
  if (isReversalTransaction(tx)) return false;
  if (amount < 0) {
    const source = getTransactionSource(tx);
    if (source === "REDEMPTION" || source === "REDEEM") return true;
    const haystack = `${tx?.source || ""} ${tx?.transactionType || ""} ${tx?.referenceType || ""} ${
      tx?.description || ""
    }`.toLowerCase();
    return haystack.includes("redeem") || haystack.includes("spend");
  }

  const source = getTransactionSource(tx);
  if (source === "REDEMPTION" || source === "REDEEM") return true;

  const haystack = `${tx?.source || ""} ${tx?.transactionType || ""} ${tx?.referenceType || ""} ${
    tx?.description || ""
  }`.toLowerCase();
  return haystack.includes("redeem") || haystack.includes("spend");
}

function getSignedAmount(tx) {
  const amount = toNumber(tx.amount ?? tx.coins);
  const type = String(tx.transactionType || tx.type || "").toLowerCase();
  const source = getTransactionSource(tx);

  if (source === "REVERSAL" || type.includes("revers")) {
    return Math.abs(amount);
  }

  if (amount > 0 && (type.includes("debit") || type.includes("spend") || type.includes("redeem"))) {
    return -Math.abs(amount);
  }
  return amount;
}

function getEventName(tx) {
  return resolveTransactionEvent(tx) || "Other";
}

function getReferralLevel(tx) {
  const level = toNumber(tx.level ?? tx.referralLevel);
  if (level >= 1 && level <= 5) return `L${level}`;

  const idempotencyKey = String(tx.idempotencyKey ?? "");
  const keyMatch = idempotencyKey.match(/SIGNUP_L([1-5])_/i);
  if (keyMatch) return `L${keyMatch[1]}`;

  const haystack = `${tx.referenceType || ""} ${tx.source || ""} ${tx.description || ""} ${idempotencyKey}`;
  const match = haystack.match(/\bL(?:evel)?[_\s-]?([1-5])\b/i);
  return match ? `L${match[1]}` : null;
}

function getReferralJoinedUserId(tx) {
  const referenceId = tx?.referenceID;
  return referenceId != null && String(referenceId).trim()
    ? String(referenceId).trim()
    : null;
}

function hasDisplayReferralCode(user) {
  const code = String(user?.referralCode ?? "").trim();
  return Boolean(code && code !== "—" && code.toLowerCase() !== "unknown");
}

function getUserAvailableCoins(user) {
  const earned = user.rewardsCoinsEarned + user.referralCoinsEarned;
  return earned - user.coinsRedeemed - user.coinsExpired - user.coinsReversed;
}

function getUserWalletBalance(user) {
  if (user?.walletBalance != null && Number.isFinite(Number(user.walletBalance))) {
    return Math.max(0, toNumber(user.walletBalance));
  }
  return Math.max(0, getUserAvailableCoins(user ?? {}));
}

function updateUserWalletBalance(user, rawTx) {
  if (rawTx?.balanceAfter == null) return;

  const txTime = new Date(rawTx.createdAt ?? rawTx.updatedAt ?? 0).getTime();
  if (Number.isNaN(txTime)) {
    user.walletBalance = toNumber(rawTx.balanceAfter);
    return;
  }

  if (user.lastTransactionAt == null || txTime >= user.lastTransactionAt) {
    user.lastTransactionAt = txTime;
    user.walletBalance = toNumber(rawTx.balanceAfter);
  }
}

function resolveRedemptionUserCoins(row, user) {
  const rowCoins = pickValue(row, [
    "availableCoins",
    "walletBalance",
    "coins",
    "balance",
    "redeemableCoins",
    "totalAvailable",
    "currentBalance",
    "blockedCoins",
  ]);
  if (rowCoins != null && rowCoins !== "") {
    return Math.max(0, toNumber(rowCoins));
  }

  return getUserWalletBalance(user);
}

function mergeUserProfile(user, profile) {
  if (!user || !profile) return user;

  const name =
    pickValue(profile, ["fullName", "name"]) ?? formatPersonName(profile?.name);
  const referralCode = pickValue(profile, ["referralCode", "referCode"]);
  const city = pickValue(profile, ["workCity", "city"]);
  const profession = pickValue(profile, ["profession"]);
  const qualification = pickValue(profile, ["qualification"]);
  const phone = pickValue(profile, ["mobileNumber", "phone"]);

  if (name) user.name = String(name);
  if (referralCode) user.referralCode = String(referralCode);
  if (city) user.city = String(city);
  if (profession) user.profession = String(profession);
  if (qualification) user.qualification = String(qualification);
  if (phone) user.phone = String(phone);

  return user;
}

function aggregateFromTransactions(transactions) {
  const usersById = new Map();
  const eventStats = new Map();
  const levelStats = REFERRAL_LEVELS.reduce((acc, level) => {
    acc[level] = { peopleJoined: new Set(), coinsIssued: 0 };
    return acc;
  }, {});

  let totalExpired = 0;
  let totalReversed = 0;
  let totalRedeemed = 0;
  let referralIssued = 0;
  let referralExpired = 0;
  let rewardsCoinsIssued = 0;
  let rewardsCoinsExpired = 0;
  const signUpWithReferralUsers = new Set();
  const signUpWithoutReferralUsers = new Set();
  const inviteSharedUsers = new Set();
  const firstApprovedShiftUsers = new Set();
  let firstApprovedShiftCoins = 0;

  transactions.forEach((rawTx) => {
    const signedAmount = getSignedAmount(rawTx);
    const absAmount = Math.abs(signedAmount);
    const userId = resolveUserId(rawTx);
    const userObj = resolveUserObject(rawTx);

    if (!usersById.has(userId)) {
      usersById.set(
        userId,
        mapAnalyticsUser({
          ...(userObj || {}),
          userId,
          user: userObj,
          transactions: [],
        })
      );
    } else if (userObj) {
      mergeUserProfile(usersById.get(userId), userObj);
    }

    const user = usersById.get(userId);
    user.transactions.push(mapTransactionRow(rawTx));
    updateUserWalletBalance(user, rawTx);

    // Reversal txs (Hubble_Reversal etc.) — count amount, do not treat as reward/redeem.
    if (isReversalTransaction(rawTx)) {
      user.coinsReversed += absAmount;
      totalReversed += absAmount;
      return;
    }

    if (signedAmount > 0) {
      if (isReferralCreditTransaction(rawTx)) {
        user.referralCoinsEarned += absAmount;
        referralIssued += absAmount;
        inviteSharedUsers.add(userId);
        const level = getReferralLevel(rawTx);
        const joinedUserId = getReferralJoinedUserId(rawTx);
        if (level) {
          user.referralLevels[level] = (user.referralLevels[level] || 0) + absAmount;
          levelStats[level].coinsIssued += absAmount;
          levelStats[level].peopleJoined.add(joinedUserId || userId);
        }
      } else if (isRewardCreditTransaction(rawTx) || getReferenceType(rawTx)) {
        user.rewardsCoinsEarned += absAmount;
        rewardsCoinsIssued += absAmount;
        const eventName = getEventName(rawTx);
        user.rewardEvents[eventName] = (user.rewardEvents[eventName] || 0) + absAmount;
        const event = eventStats.get(eventName) || { eventName, users: new Set(), coinsIssued: 0 };
        event.coinsIssued += absAmount;
        event.users.add(userId);
        eventStats.set(eventName, event);
      }

      if (isSignupWithReferralTransaction(rawTx)) {
        signUpWithReferralUsers.add(userId);
        user.usedReferralCode = true;
      }
      if (isSignupWithoutReferralTransaction(rawTx)) {
        signUpWithoutReferralUsers.add(userId);
        user.signedUpWithoutReferral = true;
      }
      if (isFirstApprovedShiftTransaction(rawTx)) {
        firstApprovedShiftCoins += absAmount;
        const shiftUserId = getFirstApprovedShiftUserId(rawTx);
        if (shiftUserId) {
          firstApprovedShiftUsers.add(shiftUserId);
          const shiftUser = usersById.get(shiftUserId);
          if (shiftUser && !shiftUser.firstApprovedShiftDate) {
            shiftUser.firstApprovedShiftDate = formatDateValue(rawTx.createdAt ?? rawTx.updatedAt);
          }
        }
      }

      if (rawTx.isExpired) {
        user.coinsExpired += absAmount;
        totalExpired += absAmount;
        if (isReferralCreditTransaction(rawTx)) referralExpired += absAmount;
        else rewardsCoinsExpired += absAmount;
      }
    } else if (signedAmount < 0) {
      if (isExpiryTransaction(rawTx)) {
        user.coinsExpired += absAmount;
        totalExpired += absAmount;
        if (isReferralCreditTransaction(rawTx)) referralExpired += absAmount;
        else rewardsCoinsExpired += absAmount;
      } else if (isRedemptionTransaction(rawTx, signedAmount)) {
        user.coinsRedeemed += absAmount;
        totalRedeemed += absAmount;
      }
    }
  });

  const users = [...usersById.values()];
  users.forEach((user) => {
    user.status = user.transactions.length > 0 ? "Active" : "Inactive";
  });

  const referredFirstShiftUsers = [...firstApprovedShiftUsers].filter((userId) =>
    signUpWithReferralUsers.has(userId)
  );

  const metrics = {
    totalCoinsEarned: users.reduce(
      (sum, user) => sum + user.rewardsCoinsEarned + user.referralCoinsEarned,
      0
    ),
    signUpsWithReferral: signUpWithReferralUsers.size,
    signUpsWithoutReferral: signUpWithoutReferralUsers.size,
    firstApprovedShifts: referredFirstShiftUsers.length,
    firstApprovedShiftCoins,
    usersEligibleToRedeem: users.filter((user) => getUserAvailableCoins(user) > 0).length,
    totalAvailable: users.reduce((sum, user) => {
      const earned = user.rewardsCoinsEarned + user.referralCoinsEarned;
      return sum + (earned - user.coinsRedeemed - user.coinsExpired - user.coinsReversed);
    }, 0),
    totalRedeemed,
    totalExpired,
    blockedByLock: 0,
    totalReversed,
    referralIssued,
    referralExpired,
    inviteSharedViaApp: inviteSharedUsers.size,
    rewardsCoinsIssued,
    rewardsCoinsExpired,
  };

  const eventRows = [...eventStats.values()]
    .map((event) => {
      const users = [...event.users]
        .map((id) => {
          const user = usersById.get(id);
          if (!user) return toUserDetail({ userId: id });
          return toUserDetail({
            ...user,
            coinsEarned: user.rewardEvents?.[event.eventName] || 0,
          });
        })
        .filter(Boolean);
      return mapEventRow([event.eventName, event.coinsIssued, users.length, users]);
    })
    .filter((row) => row.eventName);

  const levelRows = REFERRAL_LEVELS.map((level) =>
    mapLevelRow([level, levelStats[level].coinsIssued, levelStats[level].peopleJoined.size])
  );

  const successfulSignUp = signUpWithReferralUsers.size;
  // Code Captured = users in drill-down who have a visible referral code (Code: XXXX).
  const codeCaptured = users.filter(hasDisplayReferralCode).length;
  const firstApprovedShift = referredFirstShiftUsers.length;

  const filterOptions = deriveFilterOptions(users);
  return {
    metrics,
    eventRows,
    referralFunnel: buildReferralFunnel({
      codeCaptured,
      successfulSignUp,
      firstApprovedShift,
      firstApprovedShiftCoins,
    }),
    levelRows,
    users,
    rewardEvents: deriveRewardEvents(users, eventRows),
    cities: filterOptions.cities,
    professions: filterOptions.professions,
  };
}

function buildDashboardFromUsers(users, payload = {}) {
  const mappedUsers = users.map(mapAnalyticsUser).filter(Boolean);
  const eventRows = (payload.rewardEventPerformance ?? payload.eventPerformance ?? payload.events ?? [])
    .map(mapEventRow)
    .filter((row) => row.eventName);
  const levelRows = (payload.referralLevelPerformance ?? payload.levelPerformance ?? payload.levels ?? [])
    .map(mapLevelRow)
    .filter((row) => row.level);
  let referralFunnel = mapFunnelSteps(payload.referralFunnel ?? payload.funnel ?? []);

  const hasPrecomputedMetrics = Boolean(
    payload.metrics || payload.summary || payload.walletSummary || payload.rewardsPerformance
  );

  const metrics = hasPrecomputedMetrics
    ? mapMetrics({
        ...(payload.metrics ?? payload.summary ?? {}),
        ...(payload.walletSummary ?? {}),
        ...(payload.rewardsPerformance ?? {}),
        ...(payload.referralCoinSplit ?? {}),
      })
    : {
        totalCoinsEarned: mappedUsers.reduce(
          (sum, user) => sum + user.rewardsCoinsEarned + user.referralCoinsEarned,
          0
        ),
        signUpsWithReferral: mappedUsers.filter((user) => user.usedReferralCode).length,
        signUpsWithoutReferral: mappedUsers.filter((user) => !user.usedReferralCode).length,
        firstApprovedShifts: mappedUsers.filter(
          (user) => user.usedReferralCode && Boolean(user.firstApprovedShiftDate)
        ).length,
        usersEligibleToRedeem: mappedUsers.filter((user) => user.status === "Active").length,
        totalAvailable: mappedUsers.reduce((sum, user) => {
          const earned = user.rewardsCoinsEarned + user.referralCoinsEarned;
          return sum + (earned - user.coinsRedeemed - user.coinsExpired - user.coinsReversed);
        }, 0),
        totalRedeemed: mappedUsers.reduce((sum, user) => sum + user.coinsRedeemed, 0),
        totalExpired: mappedUsers.reduce((sum, user) => sum + user.coinsExpired, 0),
        blockedByLock: 0,
        totalReversed: mappedUsers.reduce((sum, user) => sum + user.coinsReversed, 0),
        referralIssued: mappedUsers.reduce((sum, user) => sum + user.referralCoinsEarned, 0),
        referralExpired: mappedUsers.reduce((sum, user) => sum + (user.referralExpired ?? 0), 0),
        inviteSharedViaApp: mappedUsers.filter((user) => user.referralCoinsEarned > 0).length,
        rewardsCoinsIssued: mappedUsers.reduce((sum, user) => sum + user.rewardsCoinsEarned, 0),
        rewardsCoinsExpired: mappedUsers.reduce((sum, user) => sum + (user.rewardsExpired ?? 0), 0),
      };

  const derivedEvents =
    eventRows.length > 0
      ? eventRows
      : [...new Set(mappedUsers.flatMap((user) => Object.keys(user.rewardEvents || {})))].map((eventName) => {
          const rewardedUsers = mappedUsers
            .filter((user) => (user.rewardEvents[eventName] || 0) > 0)
            .map((user) => toUserDetail(user))
            .filter(Boolean);
          const coinsIssued = mappedUsers.reduce(
            (sum, user) => sum + (user.rewardEvents[eventName] || 0),
            0
          );
          return mapEventRow([eventName, coinsIssued, rewardedUsers.length, rewardedUsers]);
        });

  const derivedLevels =
    levelRows.length > 0
      ? levelRows
      : REFERRAL_LEVELS.map((level) => {
          const peopleJoined = mappedUsers.filter((user) => (user.referralLevels[level] || 0) > 0).length;
          const coinsIssued = mappedUsers.reduce(
            (sum, user) => sum + (user.referralLevels[level] || 0),
            0
          );
          return mapLevelRow([level, coinsIssued, peopleJoined]);
        });

  const filterOptions = deriveFilterOptions(mappedUsers);

  if (!referralFunnel.length) {
    const successfulSignUp = mappedUsers.filter((user) => user.usedReferralCode).length;
    const codeCaptured = mappedUsers.filter(hasDisplayReferralCode).length;
    const firstApprovedShift = mappedUsers.filter(
      (user) => user.usedReferralCode && Boolean(user.firstApprovedShiftDate)
    ).length;
    referralFunnel = buildReferralFunnel({
      codeCaptured,
      successfulSignUp,
      firstApprovedShift,
      firstApprovedShiftCoins: metrics.firstApprovedShiftCoins ?? 0,
    });
  }

  return {
    metrics,
    eventRows: derivedEvents,
    referralFunnel,
    levelRows: derivedLevels,
    users: mappedUsers,
    rewardEvents: deriveRewardEvents(mappedUsers, derivedEvents),
    cities: uniqueStrings(payload.cities ?? payload.filterCities ?? filterOptions.cities),
    professions: uniqueStrings(
      payload.professions ?? payload.filterProfessions ?? filterOptions.professions
    ),
  };
}

function looksLikeUserRecord(doc) {
  if (!doc || typeof doc !== "object") return false;
  return Boolean(
    doc.transactions ||
      doc.walletTransactions ||
      doc.rewardsCoinsEarned != null ||
      doc.referralCoinsEarned != null ||
      doc.coinsRedeemed != null ||
      doc.rewardEvents
  );
}

function extractAnalyticsPayload(response) {
  const body = response?.data;
  if (!body) return null;

  const nested = body.data && typeof body.data === "object" && !Array.isArray(body.data) ? body.data : null;
  const candidates = [nested, body.analytics, body].filter(Boolean);

  for (const candidate of candidates) {
    if (
      candidate.metrics ||
      candidate.summary ||
      candidate.walletSummary ||
      candidate.userDrillDown ||
      candidate.users ||
      candidate.referralFunnel ||
      candidate.rewardEventPerformance
    ) {
      return candidate;
    }
  }

  const docs = extractListItems(response);
  if (docs.length) return { docs };

  return nested || body;
}

function mapPreAggregatedDashboard(payload) {
  const metricsSource = payload.metrics ?? payload.summary ?? payload.walletSummary ?? payload;
  const walletSource = payload.walletSummary ?? payload.redemptionSummary ?? payload;
  const rewardsSource = payload.rewardsPerformance ?? payload;
  const referralSource = payload.referralCoinSplit ?? payload;

  const metrics = mapMetrics({
    ...metricsSource,
    ...walletSource,
    ...rewardsSource,
    ...referralSource,
  });

  const eventRows = (payload.rewardEventPerformance ??
    payload.eventPerformance ??
    payload.events ??
    [])
    .map(mapEventRow)
    .filter((row) => row.eventName);

  const levelRows = (payload.referralLevelPerformance ?? payload.levelPerformance ?? payload.levels ?? [])
    .map(mapLevelRow)
    .filter((row) => row.level);

  const users = (payload.userDrillDown ?? payload.users ?? payload.drilldown ?? [])
    .map(mapAnalyticsUser)
    .filter(Boolean);

  const referralFunnel = mapFunnelSteps(payload.referralFunnel ?? payload.funnel ?? []);

  const filterOptions = {
    cities: payload.cities ?? payload.filterCities ?? payload.filters?.cities ?? [],
    professions: payload.professions ?? payload.filterProfessions ?? payload.filters?.professions ?? [],
  };

  if ((!filterOptions.cities?.length || !filterOptions.professions?.length) && users.length) {
    const derived = deriveFilterOptions(users);
    filterOptions.cities = filterOptions.cities?.length ? filterOptions.cities : derived.cities;
    filterOptions.professions = filterOptions.professions?.length
      ? filterOptions.professions
      : derived.professions;
  }

  return {
    metrics,
    eventRows,
    referralFunnel,
    levelRows,
    users,
    rewardEvents: deriveRewardEvents(
      users,
      eventRows.length ? eventRows : payload.rewardEvents ?? payload.eventNames ?? []
    ),
    cities: uniqueStrings(filterOptions.cities),
    professions: uniqueStrings(filterOptions.professions),
  };
}

function extractRedemptionData(response) {
  const body = response?.data;
  if (!body) return null;
  return body.redemptionData ?? body.data?.redemptionData ?? null;
}

function applyRedemptionData(dashboard, redemptionData) {
  if (!redemptionData || !dashboard) return dashboard;

  const eligibleRows = redemptionData.eligibleRedemptionUsersData ?? [];
  const notEligibleRows = redemptionData.notEligibleRedemptionUsersData ?? [];
  const allRedemptionRows = [...eligibleRows, ...notEligibleRows];

  const usersById = new Map(dashboard.users.map((user) => [user.id, user]));

  allRedemptionRows.forEach((row) => {
    const userId = String(row.userId ?? "").trim();
    if (!userId) return;

    let user = usersById.get(userId);
    if (!user) {
      user = mapAnalyticsUser({
        userId,
        name: row.name,
        phone: row.phone,
        city: row.city,
        profession: row.profession,
        qualification: row.qualification,
        specialisation: row.specialisation,
        transactions: [],
      });
      usersById.set(userId, user);
    } else {
      if (row.name) user.name = String(row.name);
      if (row.phone) user.phone = String(row.phone);
      if (row.city) user.city = String(row.city);
      if (row.profession) user.profession = String(row.profession);
      if (row.qualification) user.qualification = String(row.qualification);
      if (row.specialisation) user.specialisation = String(row.specialisation);
    }

    user.redemptionEligible = Boolean(row.eligible);
    user.redemptionDiffDays = toNumber(row.diffDays);
  });

  const eligibleIds = new Set(
    eligibleRows.map((row) => String(row.userId ?? "").trim()).filter(Boolean)
  );

  let redeemableCoins = 0;

  eligibleRows.forEach((row) => {
    const userId = String(row.userId ?? "").trim();
    if (!userId) return;

    const user = usersById.get(userId);
    const coins = resolveRedemptionUserCoins(row, user);
    if (user) {
      user.blockedByLockCoins = 0;
      user.redemptionEligible = true;
    }
    redeemableCoins += coins;
  });

  notEligibleRows.forEach((row) => {
    const userId = String(row.userId ?? "").trim();
    if (!userId) return;

    const user = usersById.get(userId);
    if (user) {
      user.blockedByLockCoins = 0;
      user.redemptionEligible = false;
    }
  });

  // Blocked by Lock = API notEligibleUserCount only (no coin sum / fallback).
  const blockedByLock = toNumber(redemptionData.notEligibleUserCount);
  const eligibleUserCount = toNumber(redemptionData.eligibleUserCount) || eligibleIds.size;
  const notEligibleUserCount = blockedByLock;

  const redeemableFromApi = pickNumber(redemptionData, [
    "redeemableCoins",
    "eligibleCoins",
    "totalAvailable",
  ]);

  if (redeemableCoins <= 0 && redeemableFromApi > 0) redeemableCoins = redeemableFromApi;

  const eligibleUsers = eligibleRows
    .map((row) => {
      const detail = toUserDetail(row);
      const existing = usersById.get(String(row.userId ?? "").trim());
      if (!detail) return null;
      if (!existing) return detail;
      return {
        ...detail,
        name: detail.name || existing.name,
        phone: detail.phone || existing.phone,
        profession: detail.profession || existing.profession,
        qualification: detail.qualification || existing.qualification || "",
        specialisation: detail.specialisation || existing.specialisation,
        city: detail.city || existing.city,
      };
    })
    .filter(Boolean);

  return {
    ...dashboard,
    users: [...usersById.values()],
    metrics: {
      ...dashboard.metrics,
      usersEligibleToRedeem: eligibleUserCount,
      blockedByLock,
      totalAvailable: Math.max(0, redeemableCoins),
    },
    redemptionData: {
      totalUser: toNumber(redemptionData.totalUser),
      eligibleUserCount,
      notEligibleUserCount,
      eligibleUsers,
      notEligibleUsers: notEligibleRows.map(toUserDetail).filter(Boolean),
    },
  };
}

export function getWalletTransactionListPayload({ startDate, endDate, city, profession } = {}) {
  const criteria = {};

  if (startDate) criteria.startDate = startDate;
  if (endDate) criteria.endDate = endDate;
  if (city && city !== "All") {
    criteria.workCity = city;
    criteria.city = city;
  }
  if (profession && profession !== "All") criteria.profession = profession;

  return Object.keys(criteria).length ? { criteria } : {};
}

function normalizeFilterText(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

function isActiveFilter(value) {
  const text = String(value ?? "").trim();
  return Boolean(text && text !== "All");
}

function plainText(value) {
  if (value == null) return "";
  if (typeof value === "object") return String(value.name ?? "").trim();
  return String(value).trim();
}

function getDocCity(doc) {
  const profile = resolveUserProfile(doc);
  return pickNonEmptyString(
    plainText(doc?.workCity),
    plainText(doc?.city),
    plainText(profile?.workCity),
    plainText(profile?.city),
    plainText(profile?.address?.city)
  );
}

function getDocProfession(doc) {
  const profile = resolveUserProfile(doc);
  return pickNonEmptyString(
    plainText(doc?.profession),
    plainText(profile?.profession),
    plainText(profile?.professionalType)
  );
}

function getDocTimestamp(doc) {
  const value = doc?.createdAt ?? doc?.date ?? doc?.occurredAt ?? doc?.updatedAt;
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
}

function getRangeBounds(startDate, endDate) {
  const start = startDate ? new Date(`${startDate}T00:00:00.000Z`).getTime() : null;
  const end = endDate ? new Date(`${endDate}T23:59:59.999Z`).getTime() : null;
  return {
    start: start != null && !Number.isNaN(start) ? start : null,
    end: end != null && !Number.isNaN(end) ? end : null,
  };
}

function indexProfile(index, userId, city, profession) {
  const id = String(userId ?? "").trim();
  if (!id) return;

  const existing = index.get(id) || { city: "", profession: "" };
  if (!existing.city && city) existing.city = city;
  if (!existing.profession && profession) existing.profession = profession;
  index.set(id, existing);
}

/**
 * Wallet transactions only embed the `user` profile on some rows, so city/profession
 * must be resolved per user (across every row that carries a profile) rather than
 * per transaction — otherwise a user's profile-less rows get dropped by the filter.
 */
function buildUserProfileIndex(docs, redemptionData) {
  const index = new Map();

  docs.forEach((doc) => {
    indexProfile(index, resolveUserId(doc), getDocCity(doc), getDocProfession(doc));

    const referred = doc?.referredUser;
    if (referred && typeof referred === "object") {
      indexProfile(
        index,
        pickNonEmptyString(referred._id, referred.id, doc?.referenceID),
        pickNonEmptyString(plainText(referred.workCity), plainText(referred.city)),
        plainText(referred.profession)
      );
    }
  });

  const redemptionRows = [
    ...(redemptionData?.eligibleRedemptionUsersData ?? []),
    ...(redemptionData?.notEligibleRedemptionUsersData ?? []),
  ];

  redemptionRows.forEach((row) => {
    indexProfile(
      index,
      row?.userId,
      pickNonEmptyString(plainText(row?.city), plainText(row?.workCity)),
      plainText(row?.profession)
    );
  });

  return index;
}

function resolveDocCity(doc, index) {
  return getDocCity(doc) || index?.get(resolveUserId(doc))?.city || "";
}

function resolveDocProfession(doc, index) {
  return getDocProfession(doc) || index?.get(resolveUserId(doc))?.profession || "";
}

function filterTransactionDocs(docs, { startDate, endDate, city, profession } = {}, index) {
  const { start, end } = getRangeBounds(startDate, endDate);
  const cityFilter = isActiveFilter(city) ? normalizeFilterText(city) : null;
  const professionFilter = isActiveFilter(profession) ? normalizeFilterText(profession) : null;

  if (start == null && end == null && !cityFilter && !professionFilter) return docs;

  return docs.filter((doc) => {
    const time = getDocTimestamp(doc);
    if (time != null) {
      if (start != null && time < start) return false;
      if (end != null && time > end) return false;
    }

    if (cityFilter && normalizeFilterText(resolveDocCity(doc, index)) !== cityFilter) return false;
    if (
      professionFilter &&
      normalizeFilterText(resolveDocProfession(doc, index)) !== professionFilter
    ) {
      return false;
    }

    return true;
  });
}

function filterRedemptionData(redemptionData, { city, profession } = {}, index) {
  if (!redemptionData) return redemptionData;

  const cityFilter = isActiveFilter(city) ? normalizeFilterText(city) : null;
  const professionFilter = isActiveFilter(profession) ? normalizeFilterText(profession) : null;
  if (!cityFilter && !professionFilter) return redemptionData;

  const matches = (row) => {
    const indexed = index?.get(String(row?.userId ?? "").trim());
    const rowCity = pickNonEmptyString(plainText(row?.city), plainText(row?.workCity), indexed?.city);
    const rowProfession = pickNonEmptyString(plainText(row?.profession), indexed?.profession);

    if (cityFilter && normalizeFilterText(rowCity) !== cityFilter) return false;
    if (professionFilter && normalizeFilterText(rowProfession) !== professionFilter) return false;
    return true;
  };

  const eligible = (redemptionData.eligibleRedemptionUsersData ?? []).filter(matches);
  const notEligible = (redemptionData.notEligibleRedemptionUsersData ?? []).filter(matches);

  return {
    ...redemptionData,
    totalUser: eligible.length + notEligible.length,
    eligibleUserCount: eligible.length,
    notEligibleUserCount: notEligible.length,
    eligibleRedemptionUsersData: eligible,
    notEligibleRedemptionUsersData: notEligible,
    blockedByLock: undefined,
    blockedCoins: undefined,
    notEligibleCoins: undefined,
    lockedCoins: undefined,
    redeemableCoins: undefined,
    eligibleCoins: undefined,
    totalAvailable: undefined,
  };
}

function deriveFilterOptionsFromIndex(index) {
  const values = [...index.values()];
  return {
    cities: uniqueStrings(values.map((entry) => entry.city)),
    professions: uniqueStrings(values.map((entry) => entry.profession)),
  };
}

/** Fills city/profession on aggregated users from the index so filtered rows render consistently. */
function applyProfileIndexToUsers(dashboard, index) {
  if (!index || !index.size || !dashboard?.users?.length) return dashboard;

  dashboard.users.forEach((user) => {
    const indexed = index.get(String(user.id ?? "").trim());
    if (!indexed) return;
    if (!user.city && indexed.city) user.city = indexed.city;
    if (!user.profession && indexed.profession) user.profession = indexed.profession;
  });

  return dashboard;
}

function buildFilterSummary({ total, matched, filters, defaultRange }) {
  const active = [];
  const { startDate, endDate, city, profession } = filters || {};

  if (isActiveFilter(city)) active.push(city);
  if (isActiveFilter(profession)) active.push(profession);
  if (startDate && endDate && (startDate !== defaultRange?.startDate || endDate !== defaultRange?.endDate)) {
    active.push(`${startDate} → ${endDate}`);
  }

  return {
    totalTransactions: total,
    matchedTransactions: matched,
    activeFilters: active,
    hasActiveFilters:
      isActiveFilter(city) || isActiveFilter(profession) || Boolean(startDate) || Boolean(endDate),
  };
}

export function mapWalletTransactionAnalyticsResponse(response, filters = {}) {
  if (response?.error) return { error: response.error };

  const rawRedemptionData = extractRedemptionData(response);
  const payload = extractAnalyticsPayload(response);
  if (!payload) {
    return { data: applyRedemptionData(EMPTY_ANALYTICS_DASHBOARD, rawRedemptionData) };
  }

  let dashboard;
  let allOptions = null;
  let profileIndex = null;
  let redemptionData = rawRedemptionData;
  let filterSummary = null;

  if (
    payload.metrics ||
    payload.summary ||
    payload.walletSummary ||
    payload.userDrillDown ||
    payload.users ||
    payload.referralFunnel ||
    payload.rewardEventPerformance
  ) {
    const allRows = payload.userDrillDown ?? payload.users ?? payload.drilldown ?? [];
    profileIndex = buildUserProfileIndex(allRows, rawRedemptionData);
    redemptionData = filterRedemptionData(rawRedemptionData, filters, profileIndex);

    const rows = filterTransactionDocs(allRows, filters, profileIndex);
    filterSummary = buildFilterSummary({ total: allRows.length, matched: rows.length, filters });

    if (Array.isArray(allRows) && allRows.length && rows.length !== allRows.length) {
      // A filter narrowed the drill-down rows, so metrics must be recomputed from them
      // instead of reusing the backend's unfiltered totals.
      const { metrics, summary, walletSummary, rewardsPerformance, ...rest } = payload;
      allOptions = deriveFilterOptionsFromIndex(profileIndex);
      dashboard = buildDashboardFromUsers(rows, rest);
    } else {
      dashboard = mapPreAggregatedDashboard(payload);
    }
  } else {
    const allDocs = Array.isArray(payload.docs)
      ? payload.docs
      : Array.isArray(payload)
        ? payload
        : extractListItems({ data: payload });

    profileIndex = buildUserProfileIndex(allDocs, rawRedemptionData);
    redemptionData = filterRedemptionData(rawRedemptionData, filters, profileIndex);

    // Filter option lists must stay complete even when a filter narrows the data.
    allOptions = deriveFilterOptionsFromIndex(profileIndex);
    const docs = filterTransactionDocs(allDocs, filters, profileIndex);
    filterSummary = buildFilterSummary({ total: allDocs.length, matched: docs.length, filters });

    if (docs.length) {
      dashboard = docs.some(looksLikeUserRecord)
        ? buildDashboardFromUsers(docs, payload)
        : aggregateFromTransactions(docs);
    } else {
      dashboard = { ...EMPTY_ANALYTICS_DASHBOARD, users: [], metrics: { ...EMPTY_METRICS } };
    }

    applyProfileIndexToUsers(dashboard, profileIndex);
  }

  const withRedemption = applyRedemptionData(dashboard, redemptionData);
  applyProfileIndexToUsers(withRedemption, profileIndex);

  return {
    data: {
      ...withRedemption,
      filterSummary,
      cities: allOptions?.cities?.length ? allOptions.cities : withRedemption.cities,
      professions: allOptions?.professions?.length
        ? allOptions.professions
        : withRedemption.professions,
    },
  };
}
